// i18n
import translations from './i18n.json';
import { translateInComponent } from 'infras/i18n';

import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import OddSectionContainer from 'presentations/views/components/organisms/service/OddSectionContainer';
import SectionTitle from 'presentations/views/components/molecules/ServiceSectionTitle';
import ContentItem2 from 'presentations/views/components/molecules/ServiceContentItem2';
import ContentItem4 from 'presentations/views/components/molecules/ServiceContentItem4';
import FeatureSecurity from 'presentations/views/components/organisms/service/FeatureSecurity';
import Certification from './Certification';
import { Appearance, black, gray03 } from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import Header from 'presentations/views/components/organisms/Header';
import Icon from 'presentations/views/components/atoms/Icon';
import { ViewerItem } from 'presentations/api/schema/user';
import { mediaMax, spacing } from 'presentations/views/styles';
import MainVisual from 'presentations/views/components/organisms/service/MainVisual';
import * as tracker from 'presentations/views/helpers/analytics';
import { privacyPolicyPath, termsPath } from 'presentations/routes/path';
import { ReactNode, useMemo } from 'react';

interface Props {
  viewer: ViewerItem | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const onClickInquiry = () => {
    tracker.setEvent('click corporative inquiry', {
      source: 'jicoo',
      product: 'security',
    });
  };
  const checkListItems = useMemo(
    () => [
      {
        title: t('Customer Data Protection'),
        items: [
          // t('Domain Control and account oversight'),
          t('Logical tenant separation'),
          // t('Encryption in transit (TLS 1.2+)'),
          t('Encryption at-rest (AES-256) and in transit'),
        ],
      },
      {
        title: t('Infrastructure Security'),
        items: [
          t('Hosted on leading cloud infrastructure provider'),
          t('Network and perimeter protection'),
        ],
      },
      {
        title: t('Application Protection'),
        items: [
          t('Web application firewall'),
          t('Distributed Denial of Service (DDoS) Protections'),
          t('Regular vulnerability scanning'),
          // t('Semi-annual penetration testing'),
        ],
      },
      {
        title: t('Compliance & Privacy'),
        items: [t('CCPA compliance management'), t('ISO/IEC 27001 report')],
      },
      {
        title: t('Org Security'),
        items: [
          t('Security education and awareness training'),
          t('24/7 monitoring and incident response'),
          t('Vendor risk management'),
          t('Business continuity planning'),
        ],
      },
      {
        title: t('Account Protection'),
        items: [
          t('Bot prevention'),
          // t('Login notifications'),
          // t('SSO and User lifecycle management (SCIM)'),
          t('Real-time activity (audit) log'),
          t('PII data deletion management'),
          t('Flexible admin roles'),
        ],
      },
    ],
    [],
  );

  const renderNormal = (content: ReactNode) => {
    return (
      <ThemeProvider theme={{ appearance: Appearance.Normal }}>
        {content}
      </ThemeProvider>
    );
  };

  return (
    <ServicePageTemplate
      isVisibleSupport={true}
      header={<Header viewer={props.viewer} />}
      breadcrumbs={[]}
    >
      <MainVisual
        title={t('page_title.0')}
        description={t('page_title.1')}
        onClickInquiry={onClickInquiry}
        viewer={props.viewer}
        size="large"
        isShowSignUp
      />
      <Page>
        <SectionContainerWrapper>
          <TopSectionContainer>
            <Certification />
          </TopSectionContainer>
        </SectionContainerWrapper>
        <OddSectionContainer>
          <SectionContainer>
            <SectionTitle
              title={t('enterprise_title.0')}
              description={t('enterprise_title.1')}
            />
            <ListContainer2>
              {checkListItems.map((item, i) => (
                <ContentListItem key={i}>
                  {renderNormal(
                    <ContentItem4
                      title={item.title}
                      description={
                        <CheckList>
                          {item.items.map((item, j) => (
                            <CheckListItem key={`${i}-${j}`}>
                              <CheckIcon name="check" />
                              <CheckText>{item}</CheckText>
                            </CheckListItem>
                          ))}
                        </CheckList>
                      }
                    />,
                  )}
                </ContentListItem>
              ))}
            </ListContainer2>
          </SectionContainer>
        </OddSectionContainer>
        <SectionContainer>
          <FeatureSecurity />
        </SectionContainer>
        <OddSectionContainer>
          <SectionContainer>
            <SectionTitle
              title={t('business_title.0')}
              description={t('business_title.1')}
            />
            <ListContainer1>
              <ImageContentListItem>
                <ContentItem2
                  imageUrl="pages/features/security/image1.png"
                  imageWidth={440}
                  imageHeight={288}
                  title={t('business_item_0.0')}
                  description={t('business_item_0.1')}
                />
              </ImageContentListItem>
              <ImageContentListItem>
                <ContentItem2
                  imageUrl="pages/features/security/image2.png"
                  imageWidth={440}
                  imageHeight={288}
                  title={t('business_item_1.0')}
                  description={t('business_item_1.1')}
                />
              </ImageContentListItem>
              <ImageContentListItem>
                <ContentItem2
                  imageUrl="pages/features/security/image3.png"
                  imageWidth={440}
                  imageHeight={288}
                  title={t('business_item_2.0')}
                  description={t('business_item_2.1')}
                />
              </ImageContentListItem>
              <ImageContentListItem>
                <ContentItem2
                  imageUrl="pages/features/security/image4.png"
                  imageWidth={440}
                  imageHeight={288}
                  title={t('business_item_3.0')}
                  description={t('business_item_3.1')}
                />
              </ImageContentListItem>
            </ListContainer1>
          </SectionContainer>
        </OddSectionContainer>
        <SectionContainer>
          <SectionTitle
            title={t('terms_title.0')}
            description={t('terms_title.1')}
          />
          <ListContainer2>
            <ContentListItem>
              <ContentItem4
                title={t('privacy policy')}
                description={t('privacy policy desc')}
                link={privacyPolicyPath()}
                linkTitle={t('more')}
              />
            </ContentListItem>
            <ContentListItem>
              <ContentItem4
                title={t('terms of use')}
                description={t('terms of use desc')}
                link={termsPath()}
                linkTitle={t('more')}
              />
            </ContentListItem>
            <ContentListItem>
              <ContentItem4
                title={t('security policy')}
                description={t('security policy desc')}
                link={termsPath()}
                linkTitle={t('more')}
              />
            </ContentListItem>
          </ListContainer2>
        </SectionContainer>
      </Page>
    </ServicePageTemplate>
  );
}

const Page = styled.div`
  border-bottom: 1px solid ${gray03};
`;
const SectionContainerWrapper = styled.div`
  background: url(${imagePath('pages/top/services/bg01.png')}) center 8vw
    repeat-y;
  background-size: 100vw auto;
`;
const SectionContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: ${spacing(128)} ${spacing(16)} ${spacing(96)};
  ${mediaMax.tablet} {
    padding: ${spacing(48)} ${spacing(16)};
  }
`;
const TopSectionContainer = styled(SectionContainer)`
  padding-top: 0;
  ${mediaMax.tablet} {
    padding-top: 0;
  }
`;
const ListContainer1 = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${spacing(32)};
  row-gap: ${spacing(64)};
  justify-content: space-between;
  /* margin-bottom: ${spacing(64)}; */
  ${mediaMax.tablet} {
    display: block;
  }
`;
const ListContainer2 = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: ${spacing(32)};
  row-gap: ${spacing(32)};
  justify-content: space-between;
  margin-bottom: ${spacing(64)};
  ${mediaMax.tablet} {
    display: block;
    margin-bottom: 0;
  }
`;
const ContentListItem = styled.div`
  height: auto;
  ${mediaMax.tablet} {
    width: 100%;
    margin-bottom: ${spacing(24)};
    :last-of-type {
      margin-bottom: 0;
    }
  }
`;

const ImageContentListItem = styled.div`
  height: auto;
  ${mediaMax.tablet} {
    width: 100%;
    margin-bottom: ${spacing(56)};
    :last-of-type {
      margin-bottom: 0;
    }
  }
`;

const CheckList = styled.div``;
const CheckListItem = styled.div`
  display: flex;
  align-items: center;
  color: ${black};
  /* line-height: 1.2; */
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${black} !important;
  margin-right: ${spacing(8)};
`;
const CheckText = styled.div`
  flex: 1;
`;
